import {
  Alert,
  CloseButton,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import SecondaryButton from 'app/views/components/buttons/SecondaryButton'
import { useState } from 'react'
import { SaveResult } from './user-form-logic'
import {
  userAccessLevelOptions,
  userRoleOptions,
  userStatusOptions,
} from 'app/models/dropdown-options'

const regionOptions = [
  { label: 'Americas', value: 'americas' },
  { label: 'EMEA', value: 'emea' },
  { label: 'APAC', value: 'apac' },
  { label: 'LATAM', value: 'latam' },
]

type Props = {
  form: UseFormReturnType<any, (values: any) => any>
  close: () => void
  isEdit: boolean
  onSave: () => Promise<SaveResult>
}

export default function UserFormPresentation({
  form,
  close,
  isEdit,
  onSave,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setSubmitError(null)
    setIsLoading(true)
    const result = await onSave()
    setIsLoading(false)
    if (result.success) {
      close()
    } else {
      setSubmitError(result.errorMessage)
    }
  }

  return (
    <div>
      <Group position="apart" align="center" noWrap mb="sm">
        <Text
          style={{
            fontSize: '1.5em',
            fontWeight: 700,
            color: '#32439A',
            fontFamily: 'Plus Jakarta Sans',
          }}
        >
          {isEdit ? 'Edit User' : 'New User'}
        </Text>
        <CloseButton size="lg" iconSize={20} onClick={close} />
      </Group>

      <Divider mb="sm" />

      <div>
        <div className="content">
          <form onSubmit={form.onSubmit(() => handleSubmit())}>
            <div className="create-new">
              <Stack spacing={36}>
                {isEdit ? (
                  <>
                    <Text fz="sm" fw={600} color="dimmed">
                      Read-Only Information
                    </Text>

                    <Group noWrap grow align="flex-start">
                      <TextInput
                        label="Username"
                        disabled
                        style={{ flex: 1 }}
                        {...form.getInputProps('username')}
                      />
                      <TextInput
                        label="Auth0 ID"
                        disabled
                        style={{ flex: 1 }}
                        {...form.getInputProps('id')}
                      />
                    </Group>

                    {/* Date Created hidden until backend surfaces Auth0 created_at on UserDto.
                    <Group noWrap grow align="flex-start">
                      <TextInput
                        label="Date Created"
                        disabled
                        value={form.values.createdAt}
                        style={{ flex: 1 }}
                      />
                      <div style={{ flex: 1 }} />
                    </Group>
                    */}
                  </>
                ) : (
                  <div>
                    <TextInput
                      required
                      label="Username"
                      placeholder="Enter username"
                      {...form.getInputProps('username')}
                    />
                    <Text fz="xs" color="dimmed" mt={4}>
                      Username cannot be changed after creation
                    </Text>
                  </div>
                )}

                <Text fz="sm" fw={600} color="dimmed" mt={16}>
                  Editable Information
                </Text>

                <Group noWrap grow align="flex-start">
                  <TextInput
                    required
                    label="First Name"
                    placeholder="Enter first name"
                    style={{ flex: 1 }}
                    {...form.getInputProps('firstName')}
                  />
                  <TextInput
                    required
                    label="Last Name"
                    placeholder="Enter last name"
                    style={{ flex: 1 }}
                    {...form.getInputProps('lastName')}
                  />
                </Group>

                <TextInput
                  required
                  label="Email Address"
                  placeholder="Enter email address"
                  {...form.getInputProps('email')}
                />

                <Group noWrap grow align="flex-start">
                  <Select
                    required
                    label="User Role"
                    placeholder="Select role"
                    searchable
                    data={userRoleOptions}
                    style={{ flex: 1 }}
                    {...form.getInputProps('role')}
                  />
                  <Select
                    required
                    label="Access Level"
                    placeholder="Select access level"
                    data={userAccessLevelOptions}
                    style={{ flex: 1 }}
                    {...form.getInputProps('accessLevel')}
                  />
                  <Select
                    required
                    label="Status"
                    placeholder="Select status"
                    data={userStatusOptions}
                    style={{ flex: 1 }}
                    {...form.getInputProps('status')}
                  />
                </Group>

                <TextInput
                  required
                  label="Entity/Company"
                  placeholder="Enter entity or company"
                  {...form.getInputProps('company')}
                />

                <Group noWrap grow align="flex-start">
                  <TextInput
                    label="Phone"
                    placeholder="Enter phone number"
                    style={{ flex: 1 }}
                    {...form.getInputProps('phoneNumber')}
                  />
                  <Select
                    label="Region"
                    placeholder="Select region"
                    data={regionOptions}
                    style={{ flex: 1 }}
                    {...form.getInputProps('region')}
                  />
                </Group>

                <div
                  style={{
                    backgroundColor: '#32439A1A',
                    border: '1px solid #32439A33',
                    borderRadius: 4,
                    padding: '10px 20px',
                    marginTop: 20,
                  }}
                >
                  <Text fz="sm" fw={700} style={{ color: '#32439A' }} mb={4}>
                    Auth0 Synchronization
                  </Text>
                  <Text fz="sm" style={{ color: '#32439A' }}>
                    Changes will be synchronized to Auth0. If Auth0 update fails, all changes will be rolled back.
                  </Text>
                </div>

                {submitError && (
                  <Alert color="red" title="Error" mt={16}>
                    {submitError}
                  </Alert>
                )}

                <Group position="right" mt={40}>
                  <SecondaryButton
                    variant="outline"
                    onClick={close}
                    text="Cancel"
                  />
                  <PrimaryButton
                    loading={isLoading}
                    type="submit"
                    disabled={!form.isValid()}
                    text="Save Changes"
                  />
                </Group>
              </Stack>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
